// src/components/GardenCropsListView.js
import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Button,
} from "@mui/material";
import { formatDate } from "../utils/dateUtils";

const GardenCropsListView = ({ filteredPlantedCrops, plots, handleModalOpen, onPlanCrop, garden_uuid, year, navigate, onAssignFruit }) => {
  const [selectedCropType, setSelectedCropType] = useState('vegetable');

  // Filter planted crops based on selected crop type
  const filteredCrops = useMemo(() => {
    return filteredPlantedCrops.filter((planted) => {
      const cropTypeName = planted.crop?.crop_type?.name?.toLowerCase();
      switch (selectedCropType) {
        case 'vegetable':
          return cropTypeName === 'vegetable';
        case 'fruit':
          return cropTypeName === 'fruit';
        case 'herbs-berries':
          return cropTypeName === 'herb';
        case 'cover-crops':
          return cropTypeName === 'cover crop';
        default:
          return true;
      }
    });
  }, [filteredPlantedCrops, selectedCropType]);

  const handleCropTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedCropType(newValue);
    }
  };

  // Calculate unassigned fruit tree plots
  const unassignedFruitTreePlots = useMemo(() => {
    if (selectedCropType !== 'fruit') return [];
    
    // Get all fruit tree plots
    const fruitTreePlots = plots.filter(plot => 
      plot.plot_type?.name?.toLowerCase() === 'fruit tree'
    );
    
    // Filter out plots that already have fruit crops assigned
    return fruitTreePlots.filter(plot => {
      const hasFruitCrop = filteredPlantedCrops.some(planted => 
        planted.plot_uuid === plot.plot_uuid &&
        planted.crop?.crop_type?.name?.toLowerCase() === 'fruit'
      );
      return !hasFruitCrop;
    });
  }, [plots, filteredPlantedCrops, selectedCropType]);

  const handleRowClick = (planted) => {
    const cropTypeName = planted.crop?.crop_type?.name?.toLowerCase();
    const plot = plots.find(p => p.plot_uuid === planted.plot_uuid);
    
    // For fruit and herb crops, navigate to PlotDetail
    if ((cropTypeName === 'fruit' || cropTypeName === 'herb') && plot && navigate) {
      navigate(`/gardens/${garden_uuid}/plots/${plot.plot_uuid}/detail/${year}`);
    } else {
      // For vegetables, keep existing modal behavior
      handleModalOpen(planted);
    }
  };

  return (
    <Box>
      {/* Crop Type Filter Toggle */}
      <Box 
        sx={{ 
          mb: 2,
          display: { xs: 'flex', sm: 'block' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }} 
        className="crop-type-filter-container"
      >
        <ToggleButtonGroup
          value={selectedCropType}
          exclusive
          onChange={handleCropTypeChange}
          size="small"
          className="crop-type-toggle-group"
        >
          <ToggleButton value="vegetable">Vegetables</ToggleButton>
          <ToggleButton value="fruit">Fruits</ToggleButton>
          <ToggleButton value="herbs-berries">Herbs</ToggleButton>
          <ToggleButton value="cover-crops">Cover crops</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan New Crop Button - For Vegetables and Herbs/Berries */}
      {(selectedCropType === 'vegetable' || selectedCropType === 'herbs-berries' || selectedCropType === 'cover-crops') && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onPlanCrop(null, null, null, selectedCropType)}
            className="vintage-button"
          >
            ✚ Plan New Crop
          </Button>
        </Box>
      )}

      {/* Assign Fruit Button - For Fruits when unassigned fruit tree plots exist */}
      {selectedCropType === 'fruit' && unassignedFruitTreePlots.length > 0 && onAssignFruit && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onAssignFruit(null)}
            className="vintage-button"
          >
            ✚ Assign Fruit to Tree
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Crop</TableCell>
              <TableCell>Plot</TableCell>
              <TableCell>Rows Planted</TableCell>
              <TableCell>Date Planted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCrops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  No {selectedCropType} crops found.
                </TableCell>
              </TableRow>
            ) : (
              filteredCrops.map((planted) => {
                const plot = plots.find(
                  (p) => p.plot_uuid === planted.plot_uuid
                );
                return (
                  <TableRow
                    key={planted.planted_uuid}
                    onClick={() => handleRowClick(planted)}
                    className="clickable-row"
                  >
                    <TableCell>{planted.crop?.name || "N/A"}</TableCell>
                    <TableCell>{plot ? plot.name : "N/A"}</TableCell>
                    <TableCell>
                      {planted.rows != null
                        ? planted.crop?.crop_type?.name?.toLowerCase() === "fruit"
                          ? planted.rows
                          : `${planted.rows} - (${planted.amount ?? "N/A"} pcs)`
                        : "N/A"}
                    </TableCell>
                    <TableCell>{formatDate(planted.date_planted)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GardenCropsListView;
